import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { X, MessageSquare, CheckCircle2, Ticket } from 'lucide-react';
import { collection, addDoc, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { cn } from '../lib/utils';

const CATEGORIES = ['Bug Report', 'Feature Request', 'Question'] as const;
type Category = typeof CATEGORIES[number];
type Status = 'open' | 'in_progress' | 'resolved';

const STATUS_STYLES: Record<Status, string> = {
  open: 'bg-hairline/60 text-ink-2',
  in_progress: 'bg-gold-soft text-gold-ink',
  resolved: 'bg-canvas text-accent-ink',
};
const STATUS_LABELS: Record<Status, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
};

interface SupportFormProps {
  teamId: string;
  teamName: string;
  userEmail: string;
  uid: string;
  onClose: () => void;
}

export const SupportForm = ({ teamId, teamName, userEmail, uid, onClose }: SupportFormProps) => {
  const [view, setView] = useState<'form' | 'tickets'>('form');
  const [category, setCategory] = useState<Category>('Bug Report');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tickets, setTickets] = useState<any[]>([]);

  useEffect(() => {
    if (!uid) return;
    const q = query(collection(db, 'supportTickets'), where('uid', '==', uid));
    const unsub = onSnapshot(q, snap => {
      const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      docs.sort((a: any, b: any) => (b.createdAt > a.createdAt ? 1 : -1));
      setTickets(docs);
    }, () => {});
    return unsub;
  }, [uid]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('Please describe your issue or feedback.');
      return;
    }
    setIsSubmitting(true);
    setError(null);

    const createdAt = new Date().toISOString();
    const emailBody = [
      `Category: ${category}`,
      `From: ${userEmail} (${teamName})`,
      `Team ID: ${teamId}`,
      '',
      message.trim(),
    ].join('\n');

    try {
      await Promise.all([
        addDoc(collection(db, 'supportTickets'), {
          uid,
          category,
          subject: subject.trim() || '(no subject)',
          message: message.trim(),
          userEmail,
          teamId,
          teamName,
          createdAt,
          status: 'open',
          appVersion: '1.1',
        }),
        fetch('https://formspree.io/f/xkoynywy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            category,
            subject: subject.trim() || '(no subject)',
            message: emailBody,
            _subject: `[${category}] ${subject.trim() || '(no subject)'}`,
          }),
        }).then(res => { if (!res.ok) throw new Error('Formspree error'); }),
      ]);
      setIsSuccess(true);
    } catch (err: any) {
      console.error('Failed to submit support ticket', err);
      setError('Failed to send. Check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        className="bg-canvas w-full max-w-md rounded-3xl border border-hairline shadow-2xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setView('form'); setIsSuccess(false); }}
              className={cn(
                "flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-colors",
                view === 'form' ? "text-ink" : "text-ink-2 hover:text-ink-2"
              )}
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Contact
            </button>
            <span className="text-ink-2">·</span>
            <button
              onClick={() => setView('tickets')}
              className={cn(
                "flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-colors",
                view === 'tickets' ? "text-ink" : "text-ink-2 hover:text-ink-2"
              )}
            >
              <Ticket className="w-3.5 h-3.5" />
              My Tickets
              {tickets.filter((t: any) => t.status !== 'resolved').length > 0 && (
                <span className="bg-gold text-surface text-[8px] font-black rounded-full w-4 h-4 flex items-center justify-center">
                  {tickets.filter((t: any) => t.status !== 'resolved').length}
                </span>
              )}
            </button>
          </div>
          <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form View */}
        {view === 'form' && (
          isSuccess ? (
            <div className="px-6 pb-8 pt-4 flex flex-col items-center text-center space-y-4">
              <CheckCircle2 className="w-12 h-12 text-accent-ink" />
              <div>
                <p className="font-black uppercase text-sm text-ink">Message Sent</p>
                <p className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest mt-1">
                  We'll get back to you at {userEmail}
                </p>
              </div>
              <button
                onClick={() => setView('tickets')}
                className="mt-2 px-6 py-2 bg-ink text-surface rounded-xl font-black text-xs uppercase tracking-widest hover:bg-ink transition-colors"
              >
                View My Tickets
              </button>
            </div>
          ) : (
            <div className="px-6 pb-6 space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Category</label>
                <div className="flex gap-2">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      className={cn(
                        "flex-1 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors border",
                        category === cat
                          ? "bg-ink text-surface border-ink"
                          : "bg-surface text-ink-2 border-hairline hover:border-ink-mute"
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  placeholder="Short description"
                  className="w-full bg-surface border border-hairline rounded-xl px-4 py-2 text-ink font-black outline-none focus:ring-2 focus:ring-emerald-500 transition-all placeholder:text-ink-2"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-ink-2 uppercase tracking-widest">Description <span className="text-danger">*</span></label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Describe the bug or what you'd like to see..."
                  rows={5}
                  className="w-full bg-surface border border-hairline rounded-xl px-4 py-3 text-ink font-black outline-none focus:ring-2 focus:ring-emerald-500 transition-all resize-none placeholder:text-ink-2"
                />
              </div>

              {error && (
                <p className="text-[10px] font-semibold text-danger uppercase tracking-widest">{error}</p>
              )}

              <p className="text-[10px] text-ink-2">
                Sending as {userEmail} · {teamName}
              </p>

              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full py-3 bg-ink hover:bg-ink disabled:opacity-50 text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting && <div className="w-4 h-4 border-2 border-surface/30 border-t-black rounded-full animate-spin" />}
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          )
        )}

        {/* Tickets View */}
        {view === 'tickets' && (
          <div className="px-6 pb-6">
            {tickets.length === 0 ? (
              <div className="py-10 text-center">
                <Ticket className="w-8 h-8 text-ink-2 mx-auto mb-3" />
                <p className="text-ink-2 text-xs font-semibold uppercase tracking-widest">No tickets yet</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {tickets.map((ticket: any) => (
                  <div key={ticket.id} className="bg-raised border border-hairline rounded-2xl p-4">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-xs font-black text-ink leading-tight">{ticket.subject}</p>
                      <span className={cn("text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0", STATUS_STYLES[ticket.status as Status] ?? STATUS_STYLES.open)}>
                        {STATUS_LABELS[ticket.status as Status] ?? ticket.status}
                      </span>
                    </div>
                    <p className="text-[10px] text-ink-2">{ticket.category} · {ticket.createdAt?.slice(0, 10)}</p>
                    {ticket.adminNote && (
                      <p className="mt-2 text-[10px] text-ink-2 bg-canvas rounded-xl px-3 py-2 leading-relaxed">
                        <span className="font-black text-ink-2 uppercase tracking-widest">Note: </span>{ticket.adminNote}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};
